import { getSkinIssueData } from "utils/skin/(getSkinData)";
import {
  zCreateMaxArrayLengthRule,
  zCreateMinArrayLengthRule,
  zCreateMustBeExistRule,
} from "@becon-team/becon-fe-lib/my-zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { ChangeEvent, FormEvent, ReactNode, useEffect, useState } from "react";
import { ChangeHandler, UseFormRegisterReturn, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { usePutSkinInquiry, useGetSkinInquiry } from "api/@skin/skin.query";

import {
  SkinCareSelectorEnum,
  SkinIssueDetailSelectorEnum,
  SleepTimeEnum,
  StressLevelEnum,
  YesOrNoSelectorEnum,
} from "api/@skin/skin.dto";

import type { Undefinable } from "@becon-team/becon-fe-lib/my-types";
import { SkinIssueEnum } from "api/@skin/skin.dto";

type OptionSelect = {
  value: string;
  display: string;
};

type OptionCheckbox = {
  value: string;
  display: ReactNode;
};

interface UseFormSkinInquiry {
  (args: { userId: Undefinable<string>; onSuccess?: () => void }): {
    tightAfterWashFaceOptions: OptionCheckbox[];
    isOilyAndIsDryOptions: OptionCheckbox[];
    issuesOptions: OptionCheckbox[];
    productCountOptions: OptionCheckbox[];
    usingOptions: OptionCheckbox[];
    yesOrNoOptions: OptionCheckbox[];
    sleepTimeOptions: OptionCheckbox[];
    stressOptions: OptionSelect[];
    tightAfterWashFaceRegister: UseFormRegisterReturn<"tightAfterWashFace">;
    isOilyRegister: UseFormRegisterReturn<"isOily">;
    isDryRegister: UseFormRegisterReturn<"isDry">;
    issuesRegister: UseFormRegisterReturn<"issues">;
    productCountRegister: UseFormRegisterReturn<"productCount">;
    useBodyMoistureProductRegister: UseFormRegisterReturn<"useBodyMoistureProduct">;
    sleepTimeRegister: UseFormRegisterReturn<"sleepTime">;
    stressLevelRegister: UseFormRegisterReturn<"stressLevel">;
    dietRegister: UseFormRegisterReturn<"diet">;
    smokingRegister: UseFormRegisterReturn<"smoking">;
    drinkingRegister: UseFormRegisterReturn<"drinking">;
    sunscreenRegister: UseFormRegisterReturn<"sunscreen">;
    skinCarePeriodicallyRegister: UseFormRegisterReturn<"skinCarePeriodically">;
    errorMessages: Record<
      | "tightAfterWashFace"
      | "isOily"
      | "isDry"
      | "issues"
      | "productCount"
      | "useBodyMoistureProduct"
      | "sleepTime"
      | "stressLevel"
      | "diet"
      | "smoking"
      | "drinking"
      | "sunscreen"
      | "skinCarePeriodically",
      string | undefined
    >;
    onSubmit: (event: FormEvent) => void;
    isSubmitting: boolean;
    isFormChanged: boolean;
  };
}

export const useFormPutSkinInquiry: UseFormSkinInquiry = ({ userId, onSuccess }) => {
  const { t } = useTranslation();

  const FormSchema = z.object({
    tightAfterWashFace: zCreateMustBeExistRule(t, t("세안 후 피부 당김")).and(
      z.nativeEnum(SkinIssueDetailSelectorEnum).nullable().optional(),
    ),
    isOily: zCreateMustBeExistRule(t, t("피부 번들거림")).and(
      z.nativeEnum(SkinIssueDetailSelectorEnum).nullable().optional(),
    ),
    isDry: zCreateMustBeExistRule(t, t("피부 건조함")).and(
      z.nativeEnum(SkinIssueDetailSelectorEnum).nullable().optional(),
    ),
    issues: zCreateMinArrayLengthRule(t, t("피부 고민"), 1, z.nativeEnum(SkinIssueEnum)).and(
      zCreateMaxArrayLengthRule(t, t("피부 고민"), 5, z.nativeEnum(SkinIssueEnum)),
    ),
    productCount: z.nativeEnum(SkinCareSelectorEnum).nullable().optional(),
    useBodyMoistureProduct: z.nativeEnum(YesOrNoSelectorEnum).nullable().optional(),
    sleepTime: z.nativeEnum(SleepTimeEnum).nullable().optional(),
    stressLevel: z.nativeEnum(StressLevelEnum).nullable().optional(),
    diet: z.nativeEnum(YesOrNoSelectorEnum).nullable().optional(),
    smoking: z.nativeEnum(YesOrNoSelectorEnum).nullable().optional(),
    drinking: z.nativeEnum(YesOrNoSelectorEnum).nullable().optional(),
    sunscreen: z.nativeEnum(YesOrNoSelectorEnum).nullable().optional(),
    skinCarePeriodically: z.nativeEnum(YesOrNoSelectorEnum).nullable().optional(),
  });
  type Form = z.infer<typeof FormSchema>;
  type KeyOfForm = keyof Form;

  const tightAfterWashFaceOptions = [
    { value: SkinIssueDetailSelectorEnum.NO, display: t("아니요") },
    { value: SkinIssueDetailSelectorEnum.LITTLE, display: t("조금") },
    { value: SkinIssueDetailSelectorEnum.NORMAL, display: t("보통") },
    { value: SkinIssueDetailSelectorEnum.A_LOT, display: t("많이") },
  ];
  const isOilyAndIsDryOptions = [
    { value: SkinIssueDetailSelectorEnum.NO, display: t("아니요") },
    { value: SkinIssueDetailSelectorEnum.LITTLE, display: t("조금") },
    { value: SkinIssueDetailSelectorEnum.NORMAL, display: t("보통") },
    { value: SkinIssueDetailSelectorEnum.NOT_SURE, display: t("모름") },
    { value: SkinIssueDetailSelectorEnum.A_LOT, display: t("많이") },
  ];
  const issuesOptions = Object.values(SkinIssueEnum).map((scalpIssue) => ({
    value: scalpIssue,
    display: (
      <div className="flex flex-col items-center justify-center">
        <div className="[&>svg]:w-48ptv">{getSkinIssueData(t, scalpIssue).icon}</div>
        <div className="text-md">{getSkinIssueData(t, scalpIssue).text}</div>
      </div>
    ),
  }));
  const productCountOptions = [
    { value: SkinCareSelectorEnum.NONE, display: t("없음") },
    { value: SkinCareSelectorEnum.ONE, display: t("{{n}}개", { n: 1 }) },
    { value: SkinCareSelectorEnum.TWO, display: t("{{n}}개", { n: 2 }) },
    { value: SkinCareSelectorEnum.THREE, display: t("{{n}}개", { n: 3 }) },
    { value: SkinCareSelectorEnum.FOUR_OR_MORE, display: t("{{n}}개 이상", { n: 4 }) },
  ];
  const usingOptions = [
    { value: YesOrNoSelectorEnum.NO, display: t("사용 안 함") },
    { value: YesOrNoSelectorEnum.YES, display: t("사용함") },
  ];
  const yesOrNoOptions = [
    { value: YesOrNoSelectorEnum.NO, display: t("아니요") },
    { value: YesOrNoSelectorEnum.YES, display: t("예") },
  ];
  const sleepTimeOptions = [
    { value: SleepTimeEnum.LESS_THAN_SIX, display: t("{{n}}시간 미만", { n: 6 }) },
    { value: SleepTimeEnum.MORE_THAN_SIX, display: t("{{n}}시간 이상", { n: 6 }) },
  ];
  const stressOptions = [
    { value: StressLevelEnum.NONE, display: t("없음") },
    { value: StressLevelEnum.LOW, display: t("조금") },
    { value: StressLevelEnum.MIDDLE, display: t("보통") },
    { value: StressLevelEnum.HIGH, display: t("많음") },
  ];

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof FormSchema>>({
    mode: "onSubmit",
    resolver: zodResolver(FormSchema),
  });

  const errorMessages: Record<KeyOfForm, string | undefined> = Object.fromEntries(
    Object.entries(errors).map(([key, value]) => [key as KeyOfForm, value.message]),
  ) as Record<KeyOfForm, string | undefined>;

  const issues = watch("issues");

  const onChangeIssues = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;

    if (value === SkinIssueEnum.NONE && checked) {
      setValue("issues", [SkinIssueEnum.NONE]);
    } else if (value === SkinIssueEnum.NONE && !checked) {
      setValue("issues", []);
    } else if (checked) {
      if (issues.includes(SkinIssueEnum.NONE)) {
        setValue("issues", [value as SkinIssueEnum]);
      } else if (issues?.length < 5) {
        setValue("issues", [...issues, value as SkinIssueEnum]);
      } else {
        setValue("issues", [...issues.slice(0, 4), value as SkinIssueEnum]);
      }
    } else {
      setValue(
        "issues",
        issues.filter((issue) => issue !== value),
      );
    }
  };

  const tightAfterWashFaceRegister = { ...register("tightAfterWashFace"), type: "radio" };
  const isOilyRegister = { ...register("isOily"), type: "radio" };
  const isDryRegister = { ...register("isDry"), type: "radio" };
  const issuesRegister = { ...register("issues"), type: "checkbox", onChange: onChangeIssues as ChangeHandler };
  const productCountRegister = { ...register("productCount"), type: "radio" };
  const useBodyMoistureProductRegister = { ...register("useBodyMoistureProduct"), type: "radio" };
  const sleepTimeRegister = { ...register("sleepTime"), type: "radio" };
  const stressLevelRegister = { ...register("stressLevel"), type: "radio" };
  const dietRegister = { ...register("diet"), type: "radio" };
  const smokingRegister = { ...register("smoking"), type: "radio" };
  const drinkingRegister = { ...register("drinking"), type: "radio" };
  const sunscreenRegister = { ...register("sunscreen"), type: "radio" };
  const skinCarePeriodicallyRegister = { ...register("skinCarePeriodically"), type: "radio" };

  const { mutate: putSkinInquiry, isPending: isPendingPutSkinInquiry } = usePutSkinInquiry();

  const isPending = isPendingPutSkinInquiry;

  const onSubmit = (event: FormEvent) => {
    handleSubmit(
      async ({
        tightAfterWashFace,
        isOily,
        isDry,
        issues,
        productCount,
        useBodyMoistureProduct,
        sleepTime,
        stressLevel,
        diet,
        smoking,
        drinking,
        sunscreen,
        skinCarePeriodically,
      }) => {
        if (!userId) {
          throw new Error("`userId` is undefined.");
        }
        if (!tightAfterWashFace) {
          throw new Error("`tightAfterWashFace` is undefined.");
        }
        if (!isOily) {
          throw new Error("`isOily` is undefined.");
        }
        if (!isDry) {
          throw new Error("`isDry` is undefined.");
        }
        if (!issues.length) {
          throw new Error("`issues` is undefined.");
        }
        putSkinInquiry(
          {
            userId,
            skin_issue_detail: {
              tight_after_wash_face: tightAfterWashFace,
              is_oily: isOily,
              is_dry: isDry,
            },
            issues,
            skin_care: {
              product_count: productCount || undefined,
              use_body_moisture_product: useBodyMoistureProduct || undefined,
            },
            life_style: {
              sleep_time: sleepTime || undefined,
              stress_level: stressLevel || undefined,
              diet: diet || undefined,
              smoking: smoking || undefined,
              drinking: drinking || undefined,
              sunscreen: sunscreen || undefined,
              skin_care_periodically: skinCarePeriodically || undefined,
            },
          },
          {
            onSuccess: () => {
              reset();
              onSuccess && onSuccess();
            },
          },
        );
      },
    )(event);
  };

  // side effect: 초기값 지정 및 form 변경 여부 확인
  const { data: _skinInquiry, isFetching: isFetchingSkinInquiry } = useGetSkinInquiry({ userId });

  const skinInquiry = _skinInquiry?.skin_inquiry;

  const [initialValuesJSON, setInitialValuesJSON] = useState("");
  const [isFormChanged, setIsFormChanged] = useState(false);

  const currentFormState = watch();

  useEffect(() => {
    if (isFetchingSkinInquiry) return;
    const initialValues: Form = {
      tightAfterWashFace: skinInquiry?.skin_issue_detail?.tight_after_wash_face || undefined,
      isOily: skinInquiry?.skin_issue_detail?.is_oily || undefined,
      isDry: skinInquiry?.skin_issue_detail?.is_dry || undefined,
      issues: skinInquiry?.issues || [],
      productCount: skinInquiry?.skin_care?.product_count,
      useBodyMoistureProduct: skinInquiry?.skin_care?.use_body_moisture_product,
      sleepTime: skinInquiry?.life_style?.sleep_time,
      stressLevel: skinInquiry?.life_style?.stress_level,
      diet: skinInquiry?.life_style?.diet,
      smoking: skinInquiry?.life_style?.smoking,
      drinking: skinInquiry?.life_style?.drinking,
      sunscreen: skinInquiry?.life_style?.sunscreen,
      skinCarePeriodically: skinInquiry?.life_style?.skin_care_periodically,
    };
    setInitialValuesJSON(JSON.stringify(initialValues));
    reset(initialValues);
  }, [skinInquiry, isFetchingSkinInquiry]);

  useEffect(() => {
    setIsFormChanged(JSON.stringify(currentFormState) !== initialValuesJSON);
  }, [currentFormState, initialValuesJSON]);

  return {
    tightAfterWashFaceOptions,
    isOilyAndIsDryOptions,
    issuesOptions,
    productCountOptions,
    usingOptions,
    yesOrNoOptions,
    sleepTimeOptions,
    stressOptions,
    tightAfterWashFaceRegister,
    isOilyRegister,
    isDryRegister,
    issuesRegister,
    productCountRegister,
    useBodyMoistureProductRegister,
    sleepTimeRegister,
    stressLevelRegister,
    dietRegister,
    smokingRegister,
    drinkingRegister,
    sunscreenRegister,
    skinCarePeriodicallyRegister,
    errorMessages,
    onSubmit,
    isSubmitting: isPending,
    isFormChanged,
  };
};
